import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { postService, Post, Comment } from '../services/postService';
import { followService } from '../services/followService';
import { chatService } from '../services/chatService';
import { User } from '../types';
import { useAuth } from '../hooks/useAuth';
import { formatContent } from '../utils/formatContent';
import { getMediaUrl } from '../utils/media';
import { useToast } from '../components/Toast';
import '@hsablonniere/activity-graph';
import './UserProfilePage.css';
import { useId } from 'react';

const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loadingFollow, setLoadingFollow] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [postComments, setPostComments] = useState<{ [postId: number]: Comment[] }>({});
  const [expandedPosts, setExpandedPosts] = useState<{ [postId: number]: boolean }>({});
  const [expandedContent, setExpandedContent] = useState<{ [postId: number]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [modalComments, setModalComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [likingPostId, setLikingPostId] = useState<number | null>(null);
  const [commentContent, setCommentContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [commentVideoFile, setCommentVideoFile] = useState<File | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCommentActions, setShowCommentActions] = useState(false);
  const [attachedPreviewUrl, setAttachedPreviewUrl] = useState<string | null>(null);
  const [commentVideoPreviewUrl, setCommentVideoPreviewUrl] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showGifModal, setShowGifModal] = useState(false);
  const [gifUrl, setGifUrl] = useState('');
  const [gifFile, setGifFile] = useState<File | null>(null);
  const [gifPreviewUrl, setGifPreviewUrl] = useState<string | null>(null);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  const [gifSearchResults, setGifSearchResults] = useState<any[]>([]);
  const [gifTrending, setGifTrending] = useState<any[]>([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [gifSearchMode, setGifSearchMode] = useState<'trending' | 'search'>('trending');
  // Reply states
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReplyTo, setSubmittingReplyTo] = useState<number | null>(null);
  const [replyAttachedFile, setReplyAttachedFile] = useState<File | null>(null);
  const [replyLinkUrl, setReplyLinkUrl] = useState('');
  const [replyVideoUrl, setReplyVideoUrl] = useState('');
  const [replyVideoFile, setReplyVideoFile] = useState<File | null>(null);
  const [replyShowLinkModal, setReplyShowLinkModal] = useState(false);
  const [replyShowVideoModal, setReplyShowVideoModal] = useState(false);
  const [replyShowEmojiPicker, setReplyShowEmojiPicker] = useState(false);
  const [replyAttachedPreviewUrl, setReplyAttachedPreviewUrl] = useState<string | null>(null);
  const [replyVideoPreviewUrl, setReplyVideoPreviewUrl] = useState<string | null>(null);
  const [showReplyActions, setShowReplyActions] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({});
  const [likingCommentId, setLikingCommentId] = useState<number | null>(null);
  const activityGraphRef = useRef<HTMLElement | null>(null);
  const commentActionsRef = useRef<HTMLDivElement | null>(null);
  const replyActionsRef = useRef<HTMLDivElement | null>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const commentFileInputId = useId();
  const commentVideoFileInputId = useId();
  const commentGifFileInputId = useId();
  const replyFileInputId = useId();
  const replyVideoFileInputId = useId();

  const emojiOptions = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
    '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
    '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕',
    '🤢', '🤮', '🤧', '🥵', '🥶', '😶‍🌫️', '😵', '😵‍💫', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕',
    '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱',
    '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩',
    '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'
  ];

  // Debug: Log when component mounts or username changes
  useEffect(() => {
    console.log('UserProfilePage mounted/updated, username from params:', username);
  }, [username]);

  useEffect(() => {
    const fetchData = async () => {
      if (!username) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setProfileUser(null);
        setPosts([]);

        // Decode username in case it's encoded, with error handling
        let decodedUsername = username;
        try {
          decodedUsername = decodeURIComponent(username);
        } catch (e) {
          // If decode fails, use original username
          decodedUsername = username;
        }

        console.log('Fetching profile for username:', decodedUsername);

        const userData = await userService.getUserProfileByUsername(decodedUsername);
        console.log('User data received:', userData);
        setProfileUser(userData);

        // Fetch user posts
        setLoadingPosts(true);
        console.log('Fetching posts for user ID:', userData.id);
        const postsData = await postService.getUserPosts(userData.id, { page: 1, per_page: 20 });
        console.log('Posts received:', postsData.posts.length, 'posts');
        console.log('Posts data:', postsData.posts);
        // Log each post's video_url and thumbnail_url
        postsData.posts.forEach((post, idx) => {
          console.log(`Post ${idx + 1} (ID: ${post.id}):`, {
            title: post.title,
            video_url: post.video_url,
            thumbnail_url: post.thumbnail_url,
            author_id: post.author?.id
          });
        });
        setPosts(postsData.posts);

        // Fetch comments for each post
        const commentsMap: { [postId: number]: Comment[] } = {};
        for (const post of postsData.posts) {
          try {
            const comments = await postService.getComments(post.id);
            commentsMap[post.id] = comments;
          } catch (error) {
            console.error(`Error fetching comments for post ${post.id}:`, error);
            commentsMap[post.id] = [];
          }
        }
        setPostComments(commentsMap);
      } catch (error: any) {
        console.error('Error fetching user profile:', error);
        console.error('Error details:', error.response?.data);
        setProfileUser(null);
        setPosts([]);
      } finally {
        setLoading(false);
        setLoadingPosts(false);
      }
    };

    fetchData();
  }, [username]);

  // Fetch follow status when profile user changes
  useEffect(() => {
    const fetchFollowStatus = async () => {
      if (!profileUser || !currentUser || profileUser.id === currentUser.id) {
        setIsFollowing(false);
        return;
      }

      try {
        const status = await followService.getFollowStatus(profileUser.id);
        setIsFollowing(status.is_following);
      } catch (error: any) {
        console.error('Error fetching follow status:', error);
        setIsFollowing(false);
      }
    };

    fetchFollowStatus();
  }, [profileUser, currentUser]);

  // Convert activity_map to format required by activity-graph component
  const activityGraphData = useMemo(() => {
    if (!profileUser?.stats?.activity_map) {
      return {};
    }

    const activityMap = profileUser.stats.activity_map;
    const activityMapDetailed = profileUser.stats.activity_map_detailed;
    const activityValues = Object.values(activityMap);
    const maxActivity = activityValues.length > 0 ? Math.max(...activityValues, 1) : 1;

    // Convert to activity-graph format
    const data: Record<string, { title?: string; parts?: string[] }> = {};

    Object.entries(activityMap).forEach(([dateKey, activity]) => {
      // Normalize activity to 0-4 scale for level
      const normalized = maxActivity > 0 ? Math.min(4, Math.floor((activity / maxActivity) * 4)) : 0;
      const level = normalized;

      // Create parts array for styling
      const styleParts = level > 0 ? [`level-${level}`] : [];

      // Create detailed title with posts and comments count
      let contributionText = '';
      if (activityMapDetailed && activityMapDetailed[dateKey]) {
        const detailed = activityMapDetailed[dateKey];
        const textParts: string[] = [];
        if (detailed.posts > 0) {
          textParts.push(`${detailed.posts} ${detailed.posts === 1 ? 'bài đăng' : 'bài đăng'}`);
        }
        if (detailed.comments > 0) {
          textParts.push(`${detailed.comments} ${detailed.comments === 1 ? 'bình luận' : 'bình luận'}`);
        }
        contributionText = textParts.length > 0 ? textParts.join(', ') : 'Không có hoạt động';
      } else {
        // Fallback to simple format
        contributionText = activity === 1 ? '1 đóng góp' : `${activity} đóng góp`;
      }

      data[dateKey] = {
        title: contributionText,
        parts: styleParts.length > 0 ? styleParts : undefined
      };
    });

    return data;
  }, [profileUser]);

  // Calculate start and end dates (1 year ago to today)
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const end = new Date(now);
    const start = new Date(now);
    start.setFullYear(start.getFullYear() - 1);

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  }, []);

  // Set data property on the custom element after it's mounted
  useEffect(() => {
    if (!activityGraphRef.current) return;

    const element = activityGraphRef.current as any;

    // Wait for custom element to be defined
    const setData = () => {
      if (element && typeof element.data !== 'undefined') {
        element.data = activityGraphData || {};
      }
    };

    // Try immediately
    setData();

    // Also try after a short delay in case element needs time to initialize
    const timer = setTimeout(setData, 200);

    return () => clearTimeout(timer);
  }, [activityGraphData, profileUser]);

  // Handle attached file preview
  useEffect(() => {
    if (attachedFile && attachedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(attachedFile);
      setAttachedPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setAttachedPreviewUrl(null);
  }, [attachedFile]);

  // Handle video file preview
  useEffect(() => {
    if (commentVideoFile) {
      const url = URL.createObjectURL(commentVideoFile);
      setCommentVideoPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setCommentVideoPreviewUrl(null);
  }, [commentVideoFile]);

  // Handle GIF file preview
  useEffect(() => {
    if (gifFile) {
      const url = URL.createObjectURL(gifFile);
      setGifPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setGifPreviewUrl(null);
  }, [gifFile]);

  // GIPHY API Key - using public beta key
  const GIPHY_API_KEY = 'GlVGYHkr3WSBnllca54iNt0yFbjz7L65'; // Public beta key

  // Fetch trending GIFs when modal opens
  useEffect(() => {
    if (showGifModal && gifSearchMode === 'trending' && gifTrending.length === 0) {
      fetchTrendingGifs();
    }
  }, [showGifModal, gifSearchMode]);

  // Search GIFs when query changes
  useEffect(() => {
    if (gifSearchQuery.trim() && gifSearchMode === 'search') {
      const timeoutId = setTimeout(() => {
        searchGifs(gifSearchQuery);
      }, 500); // Debounce 500ms
      return () => clearTimeout(timeoutId);
    } else if (gifSearchQuery.trim() === '' && gifSearchMode === 'search') {
      setGifSearchResults([]);
      setGifSearchMode('trending');
    }
  }, [gifSearchQuery]);

  const fetchTrendingGifs = async () => {
    try {
      setGifLoading(true);
      const response = await fetch(`https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20&rating=g`);
      const data = await response.json();
      if (data.data) {
        setGifTrending(data.data);
      }
    } catch (error) {
      console.error('Error fetching trending GIFs:', error);
    } finally {
      setGifLoading(false);
    }
  };

  const searchGifs = async (query: string) => {
    if (!query.trim()) return;
    try {
      setGifLoading(true);
      const response = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query)}&limit=20&rating=g`);
      const data = await response.json();
      if (data.data) {
        setGifSearchResults(data.data);
        setGifSearchMode('search');
      }
    } catch (error) {
      console.error('Error searching GIFs:', error);
    } finally {
      setGifLoading(false);
    }
  };

  const handleSelectGif = (gif: any) => {
    // Use the downsized_medium URL for better quality
    const gifUrlToUse = gif.images?.downsized_medium?.url || gif.images?.original?.url || gif.images?.fixed_height?.url;
    if (gifUrlToUse) {
      setGifUrl(gifUrlToUse);
      setGifFile(null);
    }
  };

  // Handle click outside comment actions
  useEffect(() => {
    if (!showCommentActions) return;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (commentActionsRef.current && !commentActionsRef.current.contains(event.target as Node)) {
        setShowCommentActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCommentActions]);

  // Focus reply textarea when replying
  useEffect(() => {
    if (replyTextareaRef.current) {
      const textarea = replyTextareaRef.current;
      const cursorPos = textarea.value.length;
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    }
  }, [replyingTo]);

  // Handle reply attached file preview
  useEffect(() => {
    if (replyAttachedFile && replyAttachedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(replyAttachedFile);
      setReplyAttachedPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setReplyAttachedPreviewUrl(null);
  }, [replyAttachedFile]);

  // Handle reply video file preview
  useEffect(() => {
    if (replyVideoFile) {
      const url = URL.createObjectURL(replyVideoFile);
      setReplyVideoPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setReplyVideoPreviewUrl(null);
  }, [replyVideoFile]);

  // Handle click outside reply actions
  useEffect(() => {
    if (!showReplyActions) return;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (replyActionsRef.current && !replyActionsRef.current.contains(event.target as Node)) {
        setShowReplyActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showReplyActions]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Hôm nay';
    if (diffDays === 1) return '1 ngày';
    if (diffDays < 7) return `${diffDays} ngày`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} tuần`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} tháng`;
    return `${Math.floor(diffDays / 365)} năm`;
  };

  const getAvatarUrl = (avatar?: string) => {
    if (!avatar) return null;
    return getMediaUrl(avatar);
  };

  const getVideoEmbedUrl = (videoUrl?: string) => {
    if (!videoUrl) return null;

    // Handle YouTube URLs
    if (videoUrl.includes('youtube.com/watch?v=')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (videoUrl.includes('youtu.be/')) {
      const videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    if (videoUrl.includes('youtube.com/embed/')) {
      return videoUrl;
    }

    // Handle other video URLs (Vimeo, etc.)
    return videoUrl;
  };

  const isLocalVideo = (videoUrl?: string) => {
    if (!videoUrl) return false;
    // Check if it's a local file path (starts with /uploads/ or relative path)
    return videoUrl.startsWith('/uploads/') || videoUrl.startsWith('./') || !videoUrl.startsWith('http');
  };

  const getVideoUrl = (videoUrl?: string) => {
    if (!videoUrl) return null;
    return getMediaUrl(videoUrl);
  };

  const isImageFile = (file: File | null) => Boolean(file && file.type.startsWith('image/'));

  const handleViewDetail = async (postId: number): Promise<void> => {
    try {
      const post = await postService.getPost(postId);
      setSelectedPost(post);
      setShowDetailModal(true);
      setCommentContent('');
      setAttachedFile(null);
      setLinkUrl('');
      setVideoUrl('');
      setCommentVideoFile(null);
      setGifUrl('');
      setGifFile(null);
      setShowLinkModal(false);
      setShowVideoModal(false);
      setShowGifModal(false);
      setShowEmojiPicker(false);
      setShowCommentActions(false);
      setGifSearchQuery('');
      setGifSearchResults([]);
      resetReplyComposer();
      await fetchComments(postId);
    } catch (err: any) {
      console.error('Error fetching post detail:', err);
    }
  };

  const fetchComments = async (postId: number) => {
    try {
      setLoadingComments(true);
      const commentsData = await postService.getComments(postId);
      console.log('UserProfilePage - Fetched comments:', commentsData);
      console.log('UserProfilePage - Comments with replies:', commentsData.map(c => ({ id: c.id, repliesCount: c.replies?.length || 0 })));
      setModalComments(commentsData);
    } catch (err: any) {
      console.error('Error fetching comments:', err);
      setModalComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async (postId: number) => {
    setLikingPostId(postId);
    try {
      const result = await postService.toggleLikePost(postId);
      // Update post in list
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, is_liked: result.is_liked, likes_count: result.likes_count }
            : post
        )
      );
      // Update selected post if it's the same
      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost({ ...selectedPost, is_liked: result.is_liked, likes_count: result.likes_count });
      }
    } catch (err: any) {
      console.error('Error liking post:', err);
    } finally {
      setTimeout(() => setLikingPostId(null), 300);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPost || (!commentContent.trim() && !attachedFile && !linkUrl && !videoUrl && !commentVideoFile && !gifUrl && !gifFile)) return;

    try {
      setSubmittingComment(true);

      // If GIF is selected, treat it as an image attachment
      let finalAttachment = attachedFile;
      if (gifFile) {
        finalAttachment = gifFile;
      }

      // If GIF URL is provided, use it as linkUrl or videoUrl depending on the URL
      let finalLinkUrl = linkUrl;
      let finalVideoUrl = videoUrl;
      if (gifUrl && !gifFile) {
        // GIF URLs are typically image URLs, so we can use linkUrl
        finalLinkUrl = gifUrl;
      }

      await postService.createComment(
        selectedPost.id,
        commentContent,
        undefined,
        {
          attachment: finalAttachment || undefined,
          linkUrl: finalLinkUrl || undefined,
          videoUrl: finalVideoUrl || undefined,
          videoFile: commentVideoFile || undefined
        }
      );

      // Clear form
      setCommentContent('');
      setAttachedFile(null);
      setLinkUrl('');
      setVideoUrl('');
      setCommentVideoFile(null);
      setGifUrl('');
      setGifFile(null);
      setShowLinkModal(false);
      setShowVideoModal(false);
      setShowGifModal(false);
      setShowEmojiPicker(false);
      setShowCommentActions(false);

      // Refresh comments
      await fetchComments(selectedPost.id);

      // Update comments count
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPost.id
            ? { ...post, comments_count: (post.comments_count || 0) + 1 }
            : post
        )
      );
      if (selectedPost) {
        setSelectedPost({ ...selectedPost, comments_count: (selectedPost.comments_count || 0) + 1 });
      }
    } catch (err: any) {
      console.error('Error adding comment:', err);
      alert(err.response?.data?.error || 'Có lỗi xảy ra khi thêm bình luận');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent, parentId: number) => {
    e.preventDefault();
    const hasExtras = replyAttachedFile || replyLinkUrl || replyVideoUrl || replyVideoFile;
    if (!selectedPost || (!replyContent.trim() && !hasExtras)) return;

    try {
      setSubmittingReplyTo(parentId);
      setSubmittingComment(true);
      await postService.createComment(
        selectedPost.id,
        replyContent,
        parentId,
        {
          attachment: replyAttachedFile || undefined,
          linkUrl: replyLinkUrl || undefined,
          videoUrl: replyVideoUrl || undefined,
          videoFile: replyVideoFile || undefined
        }
      );

      await fetchComments(selectedPost.id);

      resetReplyComposer();

      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPost.id
            ? { ...post, comments_count: (post.comments_count || 0) + 1 }
            : post
        )
      );
      if (selectedPost) {
        setSelectedPost({ ...selectedPost, comments_count: (selectedPost.comments_count || 0) + 1 });
      }
    } catch (err: any) {
      console.error('Error submitting reply:', err);
    } finally {
      setSubmittingComment(false);
      setSubmittingReplyTo(null);
    }
  };

  const handleToggleCommentLike = async (commentId: number) => {
    if (!selectedPost) return;
    setLikingCommentId(commentId);
    try {
      const result = await postService.toggleCommentLike(selectedPost.id, commentId);
      setModalComments(prev => updateCommentLikesState(prev, commentId, {
        likes_count: result.likes_count,
        is_liked: result.is_liked,
      }));
    } catch (err: any) {
      console.error('Error toggling comment like:', err);
    } finally {
      setTimeout(() => setLikingCommentId(null), 200);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!selectedPost) return;
    if (!window.confirm('Bạn có chắc chắn muốn xóa bình luận này?')) return;

    try {
      await postService.deleteComment(selectedPost.id, commentId);
      setModalComments(prev => removeCommentById(prev, commentId));

      // Update comments count
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPost.id
            ? { ...post, comments_count: Math.max(0, (post.comments_count || 0) - 1) }
            : post
        )
      );
      if (selectedPost) {
        setSelectedPost({ ...selectedPost, comments_count: Math.max(0, (selectedPost.comments_count || 0) - 1) });
      }
    } catch (err: any) {
      console.error('Error deleting comment:', err);
    }
  };

  type FlattenedReply = {
    comment: Comment;
    depth: number;
    parentAuthor?: string;
  };

  const flattenReplies = (comment: Comment, depth: number = 1): FlattenedReply[] => {
    const replies = comment.replies || [];
    const parentAuthorName = comment.author.full_name || comment.author.username;
    return replies.flatMap(reply => [
      { comment: reply, depth, parentAuthor: parentAuthorName },
      ...flattenReplies(reply, depth + 1)
    ]);
  };

  const updateCommentLikesState = (items: Comment[], commentId: number, updatedFields: Partial<Comment>): Comment[] => {
    return items.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, ...updatedFields };
      }
      if (comment.replies && comment.replies.length > 0) {
        return { ...comment, replies: updateCommentLikesState(comment.replies, commentId, updatedFields) };
      }
      return comment;
    });
  };

  const removeCommentById = (items: Comment[], commentId: number): Comment[] => {
    return items
      .filter(comment => comment.id !== commentId)
      .map(comment => {
        if (comment.replies && comment.replies.length > 0) {
          return { ...comment, replies: removeCommentById(comment.replies, commentId) };
        }
        return comment;
      });
  };

  const resetReplyComposer = () => {
    setReplyContent('');
    setReplyingTo(null);
    setReplyAttachedFile(null);
    setReplyLinkUrl('');
    setReplyVideoUrl('');
    setReplyVideoFile(null);
    setReplyShowLinkModal(false);
    setReplyShowVideoModal(false);
    setReplyShowEmojiPicker(false);
    setShowReplyActions(false);
    setReplyAttachedPreviewUrl(null);
    setReplyVideoPreviewUrl(null);
  };

  const renderReplyComposer = (parentId: number, targetAuthorName: string) => {
    const replyHasExtras = Boolean(replyAttachedFile || replyLinkUrl || replyVideoUrl || replyVideoFile);

    return (
      <form onSubmit={(e) => handleSubmitReply(e, parentId)} className="post-detail-reply-form" style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <textarea
              ref={(el) => {
                if (replyingTo === parentId) {
                  replyTextareaRef.current = el;
                } else if (replyTextareaRef.current === el) {
                  replyTextareaRef.current = null;
                }
              }}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={`Trả lời ${targetAuthorName}...`}
              rows={2}
              className="post-detail-comment-textarea"
              style={{ fontSize: '14px' }}
            />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', flexWrap: 'wrap', gap: '12px' }}>
              <input
                id={replyFileInputId}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setReplyAttachedFile(file);
                  }
                }}
              />
              <div className="relative" ref={replyActionsRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowReplyActions(prev => !prev);
                    setReplyShowEmojiPicker(false);
                  }}
                  className="post-detail-comment-action-button"
                  title="Tùy chọn trả lời"
                >
                  ⋮
                </button>

                {showReplyActions && (
                  <div className="post-detail-emoji-picker" style={{ position: 'absolute', zIndex: 20, marginTop: '8px', right: 0, width: '224px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                    <label
                      htmlFor={replyFileInputId}
                      className="post-detail-emoji-button"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '12px', cursor: 'pointer' }}
                      onClick={() => setShowReplyActions(false)}
                    >
                      📎 <span style={{ fontSize: '14px' }}>Đính kèm file</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowLinkModal(true);
                        setReplyShowVideoModal(false);
                        setReplyShowEmojiPicker(false);
                        setShowReplyActions(false);
                      }}
                      className="post-detail-emoji-button"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '12px', width: '100%', textAlign: 'left' }}
                    >
                      🔗 <span style={{ fontSize: '14px' }}>Thêm link</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowVideoModal(true);
                        setReplyShowLinkModal(false);
                        setReplyShowEmojiPicker(false);
                        setShowReplyActions(false);
                      }}
                      className="post-detail-emoji-button"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '12px', width: '100%', textAlign: 'left' }}
                    >
                      🎬 <span style={{ fontSize: '14px' }}>Thêm video</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowEmojiPicker(prev => !prev);
                        setShowReplyActions(false);
                      }}
                      className="post-detail-emoji-button"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '12px', width: '100%', textAlign: 'left' }}
                    >
                      😊 <span style={{ fontSize: '14px' }}>Chèn emoji</span>
                    </button>
                  </div>
                )}

                {replyShowEmojiPicker && (
                  <div className="post-detail-emoji-picker" style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: '8px', zIndex: 20 }}>
                    <div className="post-detail-emoji-grid">
                      {emojiOptions.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => {
                            setReplyContent(prev => prev + emoji);
                            setReplyShowEmojiPicker(false);
                          }}
                          className="post-detail-emoji-button"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(replyAttachedFile || replyLinkUrl || replyVideoUrl || replyVideoFile) && (
              <div style={{ width: '100%', marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {replyAttachedFile && (
                  <div className="post-detail-comment-attachment-item">
                    {isImageFile(replyAttachedFile) && replyAttachedPreviewUrl ? (
                      <img src={replyAttachedPreviewUrl} alt={replyAttachedFile.name} className="post-detail-comment-attachment-image" />
                    ) : (
                      <div className="post-detail-comment-attachment-file">
                        <svg className="post-detail-comment-attachment-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="post-detail-comment-attachment-name">{replyAttachedFile.name}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setReplyAttachedFile(null)}
                      className="post-detail-comment-attachment-remove"
                    >
                      ×
                    </button>
                  </div>
                )}
                {replyVideoFile && (
                  <div className="post-detail-comment-video-card">
                    {replyVideoPreviewUrl ? (
                      <video src={replyVideoPreviewUrl} controls className="post-detail-comment-video-preview" />
                    ) : (
                      <div className="post-detail-comment-video-placeholder">
                        <svg className="post-detail-comment-video-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span>{replyVideoFile.name || 'Video đã chọn'}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setReplyVideoFile(null)}
                      className="post-detail-comment-video-remove"
                    >
                      ×
                    </button>
                  </div>
                )}
                {replyLinkUrl && (
                  <div className="post-detail-comment-link-card">
                    <svg className="post-detail-comment-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span className="post-detail-comment-link-url">{replyLinkUrl}</span>
                    <button
                      type="button"
                      onClick={() => setReplyLinkUrl('')}
                      className="post-detail-comment-link-remove"
                    >
                      ×
                    </button>
                  </div>
                )}
                {replyVideoUrl && (
                  <div className="post-detail-comment-video-link-card">
                    {getVideoEmbedUrl(replyVideoUrl) ? (
                      <iframe
                        src={getVideoEmbedUrl(replyVideoUrl) || ''}
                        title="Video preview"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="post-detail-comment-video-link-iframe"
                      />
                    ) : (
                      <video src={getVideoUrl(replyVideoUrl) || ''} controls className="post-detail-comment-video-link-video" />
                    )}
                    <button
                      type="button"
                      onClick={() => setReplyVideoUrl('')}
                      className="post-detail-comment-video-link-remove"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              type="submit"
              disabled={submittingComment || (!replyContent.trim() && !replyHasExtras)}
              className="post-detail-comment-submit-button"
            >
              {submittingComment ? 'Đang gửi...' : 'Gửi'}
            </button>
            <button
              type="button"
              onClick={resetReplyComposer}
              className="post-detail-comment-submit-button"
              style={{ backgroundColor: '#f3f4f6', color: '#374151' }}
            >
              Hủy
            </button>
          </div>
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <div className="loading">Đang tải...</div>
      </div>
    );
  }

  if (!profileUser && !loading) {
    return (
      <div className="user-profile-page">
        <div className="error">Không tìm thấy người dùng với username: @{username}</div>
      </div>
    );
  }

  if (!profileUser) {
    return null;
  }

  const isOwnProfile = currentUser?.id === profileUser.id;

  // Handle follow/unfollow
  const handleFollow = async () => {
    if (!profileUser || !currentUser || loadingFollow) return;

    try {
      setLoadingFollow(true);
      await followService.followUser(profileUser.id);
      setIsFollowing(true);

      // Update followers count
      if (profileUser.stats) {
        profileUser.stats.followers_count = (profileUser.stats.followers_count || 0) + 1;
        setProfileUser({ ...profileUser });
      }

      showToast('Đã theo dõi người dùng này', 'success');
    } catch (error: any) {
      console.error('Error following user:', error);
      const errorMessage = error.response?.data?.error || 'Không thể theo dõi người dùng này';
      showToast(errorMessage, 'error');
    } finally {
      setLoadingFollow(false);
    }
  };

  const handleUnfollow = async () => {
    if (!profileUser || !currentUser || loadingFollow) return;

    try {
      setLoadingFollow(true);
      await followService.unfollowUser(profileUser.id);
      setIsFollowing(false);

      // Update followers count
      if (profileUser.stats) {
        profileUser.stats.followers_count = Math.max(0, (profileUser.stats.followers_count || 0) - 1);
        setProfileUser({ ...profileUser });
      }

      showToast('Đã bỏ theo dõi người dùng này', 'success');
    } catch (error: any) {
      console.error('Error unfollowing user:', error);
      const errorMessage = error.response?.data?.error || 'Không thể bỏ theo dõi người dùng này';
      showToast(errorMessage, 'error');
    } finally {
      setLoadingFollow(false);
    }
  };

  const handleChat = async () => {
    if (!isFollowing) {
      showToast('Vui lòng theo dõi người dùng này trước khi nhắn tin', 'warning');
      return;
    }
    if (!profileUser) {
      return;
    }
    try {
      // Tạo hoặc lấy conversation với user này
      await chatService.createOrGetConversation(profileUser.id);
      // Navigate đến trang chat với username trong URL
      navigate(`/chat/${profileUser.username}`);
    } catch (error) {
      console.error('Error creating conversation:', error);
      showToast('Không thể tạo cuộc trò chuyện', 'error');
    }
  };

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        {/* Main Content */}
        <div className="user-profile-main">
          {/* Activity Section */}
          <div className="activity-section">
            <h2 className="section-title">Hoạt động</h2>
            <div className="activity-heatmap">
              {/* Activity graph temporarily disabled due to library bug */}
              <div style={{
                padding: '20px',
                background: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                📊 Biểu đồ hoạt động đang được cập nhật
              </div>
              <div className="heatmap-legend">
                <span>Ít</span>
                <div className="legend-colors">
                  <div className="legend-color" style={{ backgroundColor: '#ebedf0' }}></div>
                  <div className="legend-color" style={{ backgroundColor: '#c6e48b' }}></div>
                  <div className="legend-color" style={{ backgroundColor: '#7bc96f' }}></div>
                  <div className="legend-color" style={{ backgroundColor: '#239a3b' }}></div>
                  <div className="legend-color" style={{ backgroundColor: '#196127' }}></div>
                </div>
                <span>Nhiều</span>
              </div>
              <p className="activity-help">Đây là gì?</p>
            </div>
          </div>

          {/* Memberships Section */}
          {profileUser.stats && profileUser.stats.memberships.length > 0 && (
            <div className="memberships-section">
              <h2 className="section-title">
                Thành viên {currentUser && currentUser.id !== profileUser.id && profileUser.stats.memberships.some(m =>
                  currentUser.stats?.memberships?.some(cm => cm.id === m.id)
                ) ? `(${profileUser.stats.memberships.filter(m =>
                  currentUser.stats?.memberships?.some(cm => cm.id === m.id)
                ).length} chung)` : ''}
              </h2>
              <div className="membership-cards">
                {profileUser.stats.memberships.map((membership, idx) => (
                  <div key={membership.id} className="membership-card">
                    <div className={`membership-logo ${idx % 2 === 1 ? 'secondary' : ''}`}>
                      <span className="logo-text">{membership.name.substring(0, 3).toUpperCase()}</span>
                    </div>
                    <div className="membership-info">
                      <h3 className="membership-name">{membership.name}</h3>
                      <p className="membership-stats">
                        {membership.members_count >= 1000
                          ? `${(membership.members_count / 1000).toFixed(1)}k`
                          : membership.members_count} thành viên • {membership.is_free ? 'Miễn phí' : `${membership.price.toLocaleString('vi-VN')} VNĐ`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contributions Section - Always show */}
          <div className="contributions-section">
            <div className="contributions-header">
              <h2 className="section-title">
                {loadingPosts ? 'Đang tải...' : `${posts.length} đóng góp cho TaoThaoAIPro`}
              </h2>
              <div className="contributions-controls">
                <select className="contributions-filter">
                  <option>Đóng góp cho: TaoThaoAIPro</option>
                </select>
                <button className="filter-button">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>
              </div>
            </div>

            {loadingPosts ? (
              <div className="loading-posts">Đang tải bài đăng...</div>
            ) : posts.length === 0 ? (
              <div className="no-posts">
                <p>Chưa có đóng góp nào.</p>
                <p className="text-sm text-gray-500 mt-2">Người dùng này chưa đăng bài viết nào.</p>
              </div>
            ) : (
              <div className="contributions-list">
                {posts.map((post) => {
                  const comments = postComments[post.id] || [];
                  const isExpanded = expandedPosts[post.id] || false;
                  const recentComments = comments.slice(0, 3);
                  const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

                  // Get unique comment authors for recent activity
                  const recentActivityUsers = Array.from(
                    new Set(comments.slice(-5).map(c => c.author.id))
                  ).slice(0, 5).map(id => {
                    const comment = comments.find(c => c.author.id === id);
                    return comment?.author;
                  }).filter(Boolean);

                  return (
                    <div
                      key={post.id}
                      className="contribution-item"
                      onClick={() => handleViewDetail(post.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="contribution-header">
                        <div className="contribution-author">
                          <div className="author-avatar-wrapper">
                            {profileUser.avatar ? (
                              <img
                                src={getAvatarUrl(profileUser.avatar) || ''}
                                alt={profileUser.full_name || profileUser.username}
                                className="author-avatar"
                              />
                            ) : (
                              <div className="author-avatar placeholder">
                                {(profileUser.full_name || profileUser.username)[0].toUpperCase()}
                              </div>
                            )}
                            <div className="author-level-badge">{profileUser.stats?.level || 1}</div>
                          </div>
                          <div className="author-info">
                            <span className="author-name">{profileUser.full_name || profileUser.username}</span>
                            <span className="contribution-time">{formatDate(post.created_at)} • Trò chuyện thành viên</span>
                          </div>
                        </div>
                      </div>
                      <h3 className="contribution-title">{post.title}</h3>

                      {/* Content and Media Layout - Text left, Media right */}
                      <div className="contribution-body-layout">
                        <div className="contribution-text-content">
                          <p className="contribution-content">
                            {expandedContent[post.id] || post.content.length < 300
                              ? post.content
                              : `${post.content.substring(0, 300)}...`}
                          </p>
                          {!expandedContent[post.id] && post.content.length > 300 && (
                            <button
                              className="read-more-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedContent({ ...expandedContent, [post.id]: true });
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              Đọc thêm
                            </button>
                          )}
                          {expandedContent[post.id] && post.content.length > 300 && (
                            <button
                              className="read-less-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedContent({ ...expandedContent, [post.id]: false });
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              Thu gọn
                            </button>
                          )}
                        </div>

                        {/* Video or Thumbnail - Right side - Always show if exists */}
                        {(post.video_url || post.thumbnail_url) && (
                          <div className="contribution-media-sidebar">
                            {post.video_url && (() => {
                              const videoUrl = getVideoUrl(post.video_url);
                              const isLocal = isLocalVideo(post.video_url);
                              console.log('Rendering video:', {
                                original: post.video_url,
                                processed: videoUrl,
                                isLocal,
                                thumbnail: post.thumbnail_url
                              });
                              return (
                                <div className="contribution-video">
                                  {isLocal ? (
                                    // Local video file - use video tag
                                    <div className="video-container-local">
                                      <video
                                        controls
                                        className="contribution-video-player"
                                        poster={post.thumbnail_url ? getAvatarUrl(post.thumbnail_url) || undefined : undefined}
                                      >
                                        <source src={videoUrl || ''} type="video/mp4" />
                                        Trình duyệt của bạn không hỗ trợ video tag.
                                      </video>
                                    </div>
                                  ) : getVideoEmbedUrl(post.video_url) ? (
                                    // YouTube or other embed URLs - use iframe
                                    <div className="video-container">
                                      <iframe
                                        src={getVideoEmbedUrl(post.video_url) || ''}
                                        title={post.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                      ></iframe>
                                    </div>
                                  ) : (
                                    // Fallback - link to video
                                    <div className="video-placeholder">
                                      <a href={getVideoUrl(post.video_url) || ''} target="_blank" rel="noopener noreferrer">
                                        Xem video
                                      </a>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {post.thumbnail_url && !post.video_url && (() => {
                              const thumbUrl = getAvatarUrl(post.thumbnail_url);
                              console.log('Rendering thumbnail:', { original: post.thumbnail_url, processed: thumbUrl });
                              return (
                                <div className="contribution-thumbnail">
                                  <img
                                    src={thumbUrl || ''}
                                    alt={post.title}
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Engagement Metrics */}
                      <div className="contribution-engagement">
                        <div className="engagement-metrics">
                          <button className="engagement-button like">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                            <span>{post.likes_count}</span>
                          </button>
                          <button className="engagement-button comment">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            <span>{post.comments_count}</span>
                          </button>
                        </div>

                        {/* Recent Activity */}
                        {recentActivityUsers.length > 0 && lastComment && (
                          <div className="recent-activity">
                            <div className="activity-avatars">
                              {recentActivityUsers.map((user, idx) => (
                                user && (
                                  <div key={idx} className="activity-avatar" style={{ zIndex: recentActivityUsers.length - idx }}>
                                    {user.avatar ? (
                                      <img src={getAvatarUrl(user.avatar) || ''} alt={user.full_name || user.username} />
                                    ) : (
                                      <div className="activity-avatar-placeholder">
                                        {(user.full_name || user.username)[0].toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                )
                              ))}
                            </div>
                            <span className="last-comment-time">Bình luận cuối {formatDate(lastComment.created_at)} trước</span>
                          </div>
                        )}
                      </div>

                      {/* Comments Section */}
                      {comments.length > 0 && (
                        <div className="contribution-comments">
                          {!isExpanded && recentComments.length > 0 && (
                            <div className="comments-preview">
                              {recentComments.map((comment) => (
                                <div key={comment.id} className="comment-item">
                                  <div className="comment-author-avatar">
                                    {comment.author.avatar ? (
                                      <img src={getAvatarUrl(comment.author.avatar) || ''} alt={comment.author.full_name || comment.author.username} />
                                    ) : (
                                      <div className="comment-avatar-placeholder">
                                        {(comment.author.full_name || comment.author.username)[0].toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="comment-content">
                                    <div className="comment-header">
                                      <span className="comment-author-name">{comment.author.full_name || comment.author.username}</span>
                                      <span className="comment-meta">
                                        {comment.likes_count || 0} thích • {formatDate(comment.created_at)}
                                      </span>
                                    </div>
                                    <p className="comment-text">{comment.content}</p>
                                    {comment.replies && comment.replies.length > 0 && (
                                      <div className="comment-replies">
                                        {comment.replies.slice(0, 2).map((reply) => (
                                          <div key={reply.id} className="reply-item">
                                            <div className="reply-author-avatar">
                                              {reply.author.avatar ? (
                                                <img src={getAvatarUrl(reply.author.avatar) || ''} alt={reply.author.full_name || reply.author.username} />
                                              ) : (
                                                <div className="reply-avatar-placeholder">
                                                  {(reply.author.full_name || reply.author.username)[0].toUpperCase()}
                                                </div>
                                              )}
                                            </div>
                                            <div className="reply-content">
                                              <span className="reply-author-name">{reply.author.full_name || reply.author.username}</span>
                                              <span className="reply-text">{reply.content}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {comments.length > 3 && (
                                <button
                                  className="view-more-comments"
                                  onClick={() => setExpandedPosts({ ...expandedPosts, [post.id]: true })}
                                >
                                  Xem thêm {comments.length - 3} bình luận
                                </button>
                              )}
                            </div>
                          )}

                          {isExpanded && (
                            <div className="comments-full">
                              {comments.map((comment) => (
                                <div key={comment.id} className="comment-item">
                                  <div className="comment-author-avatar">
                                    {comment.author.avatar ? (
                                      <img src={getAvatarUrl(comment.author.avatar) || ''} alt={comment.author.full_name || comment.author.username} />
                                    ) : (
                                      <div className="comment-avatar-placeholder">
                                        {(comment.author.full_name || comment.author.username)[0].toUpperCase()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="comment-content">
                                    <div className="comment-header">
                                      <span className="comment-author-name">{comment.author.full_name || comment.author.username}</span>
                                      <span className="comment-meta">
                                        {comment.likes_count || 0} thích • {formatDate(comment.created_at)}
                                      </span>
                                    </div>
                                    <p className="comment-text">{comment.content}</p>
                                    {comment.replies && comment.replies.length > 0 && (
                                      <div className="comment-replies">
                                        {comment.replies.map((reply) => (
                                          <div key={reply.id} className="reply-item">
                                            <div className="reply-author-avatar">
                                              {reply.author.avatar ? (
                                                <img src={getAvatarUrl(reply.author.avatar) || ''} alt={reply.author.full_name || reply.author.username} />
                                              ) : (
                                                <div className="reply-avatar-placeholder">
                                                  {(reply.author.full_name || reply.author.username)[0].toUpperCase()}
                                                </div>
                                              )}
                                            </div>
                                            <div className="reply-content">
                                              <span className="reply-author-name">{reply.author.full_name || reply.author.username}</span>
                                              <span className="reply-text">{reply.content}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <button
                                className="view-less-comments"
                                onClick={() => setExpandedPosts({ ...expandedPosts, [post.id]: false })}
                              >
                                Thu gọn
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="user-profile-sidebar">
          <div className="profile-card">
            <div className="profile-avatar-container">
              {profileUser.avatar ? (
                <img
                  src={getAvatarUrl(profileUser.avatar) || ''}
                  alt={profileUser.full_name || profileUser.username}
                  className="profile-avatar"
                />
              ) : (
                <div className="profile-avatar placeholder">
                  {(profileUser.full_name || profileUser.username)[0].toUpperCase()}
                </div>
              )}
              <div className="level-badge">{profileUser.stats?.level || 1}</div>
              <div className="level-ring" style={{
                borderColor: '#10b981',
                borderTopColor: 'transparent',
                borderRightColor: 'transparent',
                transform: `rotate(${profileUser.stats ? -45 + (360 * (profileUser.stats.points % 100) / 100) : -45}deg)`
              }}></div>
            </div>
            <div className="profile-level">
              <span className="level-text">Cấp độ {profileUser.stats?.level || 1}</span>
              <span className="level-progress">{profileUser.stats?.points_to_next || 100} điểm để lên cấp</span>
            </div>
            <h2 className="profile-name">{profileUser.full_name || profileUser.username}</h2>
            <p className="profile-username">@{profileUser.username}</p>
            {profileUser.address && (
              <p className="profile-bio">{profileUser.address}</p>
            )}
            <div className="profile-status">
              <span className="status-text">
                Hoạt động {(() => {
                  // Find most recent activity from posts
                  if (posts.length > 0) {
                    const mostRecent = posts[0].created_at;
                    return formatDate(mostRecent);
                  }
                  return formatDate(profileUser.created_at);
                })()} trước
              </span>
            </div>
            <div className="profile-joined">
              <span>Tham gia {new Date(profileUser.created_at).toLocaleDateString('vi-VN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-value">{profileUser.stats?.posts_count || posts.length}</span>
                <span className="stat-label">Đóng góp</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{profileUser.stats?.followers_count || 0}</span>
                <span className="stat-label">Người theo dõi</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{profileUser.stats?.following_count || 0}</span>
                <span className="stat-label">Đang theo dõi</span>
              </div>
            </div>
            {!isOwnProfile && (
              <div className="profile-actions">
                <button
                  className={`follow-button ${isFollowing ? 'following' : ''} ${loadingFollow ? 'loading' : ''}`}
                  onClick={isFollowing ? handleUnfollow : handleFollow}
                  disabled={loadingFollow}
                >
                  {loadingFollow ? (
                    <>
                      <span className="button-spinner"></span>
                      <span>Đang xử lý...</span>
                    </>
                  ) : isFollowing ? (
                    <>
                      <svg className="button-icon check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="follow-text">Đã theo dõi</span>
                      <span className="unfollow-text">Bỏ theo dõi</span>
                    </>
                  ) : (
                    <>
                      <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Theo dõi</span>
                    </>
                  )}
                </button>
                <button
                  className={`chat-button ${!isFollowing ? 'disabled' : ''}`}
                  onClick={handleChat}
                  disabled={!isFollowing}
                  title={!isFollowing ? 'Vui lòng theo dõi để nhắn tin' : ''}
                >
                  <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Trò chuyện</span>
                </button>
              </div>
            )}
          </div>
          <div className="profile-footer">
            <p>Powered by TaoThaoAIPro</p>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedPost && (
        <div className="post-detail-modal-overlay" onClick={() => {
          setShowDetailModal(false);
          setSelectedPost(null);
          setModalComments([]);
          resetReplyComposer();
        }}>
          <div className="post-detail-modal" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <div className="post-detail-modal-header">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedPost(null);
                  setModalComments([]);
                  setCommentContent('');
                  setAttachedFile(null);
                  setLinkUrl('');
                  setVideoUrl('');
                  setCommentVideoFile(null);
                  setShowLinkModal(false);
                  setShowVideoModal(false);
                  setShowGifModal(false);
                  setShowEmojiPicker(false);
                  setShowCommentActions(false);
                }}
                className="post-detail-modal-close"
              >
                ×
              </button>
            </div>

            {/* Main Content */}
            <div className="post-detail-modal-content">
              {/* Author Info */}
              <div className="post-detail-author">
                <Link
                  to={`/u/${encodeURIComponent(selectedPost.author.username)}`}
                  className="post-detail-author-avatar"
                >
                  {selectedPost.author.avatar ? (
                    <img src={getAvatarUrl(selectedPost.author.avatar) || ''} alt={selectedPost.author.full_name || selectedPost.author.username} />
                  ) : (
                    <div className="post-detail-avatar-placeholder">
                      {(selectedPost.author.full_name || selectedPost.author.username)[0].toUpperCase()}
                    </div>
                  )}
                </Link>
                <div className="post-detail-author-info">
                  <div className="post-detail-author-name-row">
                    <Link
                      to={`/u/${encodeURIComponent(selectedPost.author.username)}`}
                      className={`post-detail-author-name ${selectedPost.author.role === 'admin' ? 'admin' : ''
                        }`}
                    >
                      {selectedPost.author.full_name || selectedPost.author.username}
                    </Link>
                    <span className="post-detail-author-badge">Tác giả</span>
                    {selectedPost.author.role === 'admin' && (
                      <span className="post-detail-admin-badge">Quản trị viên</span>
                    )}
                  </div>
                  <div className="post-detail-author-meta">
                    <span>{formatDate(selectedPost.created_at)}</span>
                    {selectedPost.tags && selectedPost.tags.length > 0 && (
                      <>
                        <span>•</span>
                        <span>{selectedPost.tags[0]}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Title */}
              <h1 className="post-detail-title">{selectedPost.title}</h1>

              {/* Content - Formatted */}
              <div className="post-detail-content">
                {formatContent(selectedPost.content)}
              </div>

              {/* Video/Media - Below content */}
              {(selectedPost.video_url || selectedPost.thumbnail_url) && (
                <div className="post-detail-media">
                  {selectedPost.video_url && (() => {
                    const videoUrl = getVideoUrl(selectedPost.video_url);
                    const isLocal = isLocalVideo(selectedPost.video_url);
                    return (
                      <div className="post-detail-video-wrapper">
                        {isLocal ? (
                          <div className="post-detail-video-container-local">
                            <video
                              controls
                              className="post-detail-video-player"
                              poster={selectedPost.thumbnail_url ? getAvatarUrl(selectedPost.thumbnail_url) || undefined : undefined}
                            >
                              <source src={videoUrl || ''} type="video/mp4" />
                              Trình duyệt của bạn không hỗ trợ video tag.
                            </video>
                          </div>
                        ) : getVideoEmbedUrl(selectedPost.video_url) ? (
                          <div className="post-detail-video-container">
                            <iframe
                              src={getVideoEmbedUrl(selectedPost.video_url) || ''}
                              title={selectedPost.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        ) : (
                          <div className="post-detail-video-placeholder">
                            <a href={getVideoUrl(selectedPost.video_url) || ''} target="_blank" rel="noopener noreferrer">
                              Xem video
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {selectedPost.thumbnail_url && !selectedPost.video_url && (
                    <div className="post-detail-thumbnail-wrapper">
                      <img
                        src={getAvatarUrl(selectedPost.thumbnail_url) || ''}
                        alt={selectedPost.title}
                        className="post-detail-thumbnail-img"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Engagement Bar */}
              <div className="post-detail-engagement">
                <div className="post-detail-engagement-buttons">
                  <button
                    onClick={() => handleLike(selectedPost.id)}
                    className={`post-detail-like-button ${selectedPost.is_liked ? 'liked' : ''} ${likingPostId === selectedPost.id ? 'loading' : ''}`}
                  >
                    <svg
                      className="post-detail-like-icon"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      fill={selectedPost.is_liked ? 'currentColor' : 'none'}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>{selectedPost.likes_count}</span>
                  </button>
                  <button className="post-detail-comment-button">
                    <span>💬</span>
                    <span>{selectedPost.comments_count} bình luận</span>
                  </button>
                </div>
              </div>

              {/* Comments Section */}
              <div className="post-detail-comments-section">
                <h3 className="post-detail-comments-title">
                  Bình luận ({selectedPost.comments_count})
                </h3>

                {/* Comment Form */}
                {currentUser && (
                  <form onSubmit={handleAddComment} className="post-detail-comment-form">
                    <div className="post-detail-comment-form-avatar">
                      {currentUser.avatar ? (
                        <img src={getAvatarUrl(currentUser.avatar) || ''} alt={currentUser.full_name || currentUser.username} />
                      ) : (
                        <div className="post-detail-comment-form-avatar-placeholder">
                          {(currentUser.full_name || currentUser.username)[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="post-detail-comment-form-content">
                      <textarea
                        value={commentContent}
                        onChange={(e) => setCommentContent(e.target.value)}
                        placeholder="Viết bình luận..."
                        rows={3}
                        className="post-detail-comment-textarea"
                      />
                      <div className="post-detail-comment-form-actions-wrapper">
                        <input
                          id={commentFileInputId}
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setAttachedFile(file);
                            }
                          }}
                        />
                        <div className="post-detail-comment-actions-buttons">
                          <label
                            htmlFor={commentFileInputId}
                            className="post-detail-comment-action-button"
                            title="Đính kèm file"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📎
                          </label>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowLinkModal(true);
                            }}
                            className="post-detail-comment-action-button"
                            title="Thêm link"
                          >
                            🔗
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowVideoModal(true);
                            }}
                            className="post-detail-comment-action-button"
                            title="Thêm video"
                          >
                            🎬
                          </button>
                          <div className="relative" ref={commentActionsRef}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowEmojiPicker(prev => !prev);
                              }}
                              className="post-detail-comment-action-button"
                              title="Chèn emoji"
                            >
                              😊
                            </button>
                            {showEmojiPicker && (
                              <div className="post-detail-emoji-picker" onClick={(e) => e.stopPropagation()}>
                                <div className="post-detail-emoji-grid">
                                  {emojiOptions.map((emoji) => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCommentContent(prev => prev + emoji);
                                        setShowEmojiPicker(false);
                                      }}
                                      className="post-detail-emoji-button"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowGifModal(true);
                            }}
                            className="post-detail-comment-action-button"
                            title="Thêm GIF"
                          >
                            GIF
                          </button>
                        </div>

                        {(attachedFile || commentVideoFile || linkUrl || videoUrl || gifUrl || gifFile) && (
                          <div className="post-detail-comment-attachments" style={{ width: '100%', marginTop: '8px' }}>
                            {attachedFile && (
                              <div className="post-detail-comment-attachment-item">
                                {isImageFile(attachedFile) && attachedPreviewUrl ? (
                                  <img src={attachedPreviewUrl} alt={attachedFile.name} className="post-detail-comment-attachment-image" />
                                ) : (
                                  <div className="post-detail-comment-attachment-file">
                                    <svg className="post-detail-comment-attachment-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <span className="post-detail-comment-attachment-name">{attachedFile.name}</span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAttachedFile(null);
                                  }}
                                  className="post-detail-comment-attachment-remove"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {commentVideoFile && (
                              <div className="post-detail-comment-video-card">
                                {commentVideoPreviewUrl ? (
                                  <video src={commentVideoPreviewUrl} controls className="post-detail-comment-video-preview" />
                                ) : (
                                  <div className="post-detail-comment-video-placeholder">
                                    <svg className="post-detail-comment-video-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    <span>{commentVideoFile.name || 'Video đã chọn'}</span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCommentVideoFile(null);
                                  }}
                                  className="post-detail-comment-video-remove"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {linkUrl && (
                              <div className="post-detail-comment-link-card">
                                <svg className="post-detail-comment-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <span className="post-detail-comment-link-url">{linkUrl}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLinkUrl('');
                                  }}
                                  className="post-detail-comment-link-remove"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {videoUrl && (
                              <div className="post-detail-comment-video-link-card">
                                {getVideoEmbedUrl(videoUrl) ? (
                                  <iframe
                                    src={getVideoEmbedUrl(videoUrl) || ''}
                                    title="Video preview"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="post-detail-comment-video-link-iframe"
                                  />
                                ) : (
                                  <video src={getVideoUrl(videoUrl) || ''} controls className="post-detail-comment-video-link-video" />
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setVideoUrl('');
                                  }}
                                  className="post-detail-comment-video-link-remove"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {gifFile && (
                              <div className="post-detail-comment-attachment-item">
                                {gifPreviewUrl ? (
                                  <img src={gifPreviewUrl} alt="GIF preview" className="post-detail-comment-attachment-image" />
                                ) : (
                                  <div className="post-detail-comment-attachment-file">
                                    <svg className="post-detail-comment-attachment-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="post-detail-comment-attachment-name">{gifFile.name || 'GIF đã chọn'}</span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGifFile(null);
                                  }}
                                  className="post-detail-comment-attachment-remove"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                            {gifUrl && !gifFile && (
                              <div className="post-detail-comment-attachment-item">
                                <img src={gifUrl} alt="GIF" className="post-detail-comment-attachment-image" />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGifUrl('');
                                  }}
                                  className="post-detail-comment-attachment-remove"
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={submittingComment || (!commentContent.trim() && !attachedFile && !linkUrl && !videoUrl && !commentVideoFile && !gifUrl && !gifFile)}
                          className="post-detail-comment-submit-button"
                          style={{ marginLeft: 'auto' }}
                        >
                          {submittingComment ? 'Đang gửi...' : 'Gửi'}
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                {/* Comments List */}
                {loadingComments ? (
                  <div className="post-detail-comments-loading">Đang tải bình luận...</div>
                ) : modalComments.length === 0 ? (
                  <div className="post-detail-comments-empty">Chưa có bình luận nào</div>
                ) : (
                  <div className="post-detail-comments-list">
                    {modalComments.map((comment) => {
                      const replyThread = flattenReplies(comment);
                      const isExpanded = expandedComments[comment.id];
                      const maxCollapsedReplies = 2;
                      const visibleReplyThread = isExpanded ? replyThread : replyThread.slice(0, maxCollapsedReplies);

                      return (
                        <div key={comment.id} className="post-detail-comment-item">
                          <Link
                            to={`/u/${encodeURIComponent(comment.author.username)}`}
                            className="post-detail-comment-avatar"
                          >
                            {comment.author.avatar ? (
                              <img src={getAvatarUrl(comment.author.avatar) || ''} alt={comment.author.full_name || comment.author.username} />
                            ) : (
                              <div className="post-detail-comment-avatar-placeholder">
                                {(comment.author.full_name || comment.author.username)[0].toUpperCase()}
                              </div>
                            )}
                          </Link>
                          <div className="post-detail-comment-content">
                            <div className="post-detail-comment-header">
                              <Link
                                to={`/u/${encodeURIComponent(comment.author.username)}`}
                                className={`post-detail-comment-author-name ${comment.author.role === 'admin' ? 'admin' : ''}`}
                              >
                                {comment.author.full_name || comment.author.username}
                              </Link>
                              {selectedPost && comment.author.id === selectedPost.author.id && (
                                <span style={{ padding: '2px 6px', backgroundColor: '#dbeafe', color: '#1e40af', fontSize: '12px', fontWeight: 500, borderRadius: '4px', marginLeft: '8px' }}>
                                  Tác giả
                                </span>
                              )}
                              {comment.author.role === 'admin' && (
                                <span style={{ padding: '2px 6px', backgroundColor: '#fee2e2', color: '#dc2626', fontSize: '12px', fontWeight: 500, borderRadius: '4px', marginLeft: '8px' }}>
                                  Quản trị viên
                                </span>
                              )}
                              <span className="post-detail-comment-meta">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="post-detail-comment-text">{formatContent(comment.content)}</p>
                            {comment.attachment_url && (
                              <div style={{ marginTop: '8px' }}>
                                <img src={getAvatarUrl(comment.attachment_url) || ''} alt="Attachment" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                              </div>
                            )}
                            {comment.link_url && (
                              <div style={{ marginTop: '8px' }}>
                                <a href={comment.link_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '14px', textDecoration: 'underline' }}>
                                  {comment.link_url}
                                </a>
                              </div>
                            )}
                            {comment.video_url && (
                              <div style={{ marginTop: '8px' }}>
                                {getVideoEmbedUrl(comment.video_url) ? (
                                  <iframe
                                    src={getVideoEmbedUrl(comment.video_url) || ''}
                                    title="Video"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{ width: '100%', maxWidth: '400px', height: '225px', borderRadius: '8px' }}
                                  />
                                ) : (
                                  <video src={getVideoUrl(comment.video_url) || ''} controls style={{ maxWidth: '400px', borderRadius: '8px' }} />
                                )}
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                              <button
                                onClick={() => handleToggleCommentLike(comment.id)}
                                disabled={likingCommentId === comment.id}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: comment.is_liked ? '#f43f5e' : '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                              >
                                <svg
                                  style={{ width: '16px', height: '16px' }}
                                  viewBox="0 0 24 24"
                                  fill={comment.is_liked ? 'currentColor' : 'none'}
                                  stroke="currentColor"
                                  strokeWidth={1.5}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21.8 7.112c0-2.632-2.086-4.762-4.66-4.762-1.704 0-3.188.988-3.9 2.43-.712-1.442-2.196-2.43-3.9-2.43-2.574 0-4.66 2.13-4.66 4.762 0 6.138 8.56 9.92 8.56 9.92s8.56-3.782 8.56-9.92z" />
                                </svg>
                                <span>{comment.likes_count ?? 0}</span>
                              </button>
                              <button
                                onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                                style={{ fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                              >
                                Trả lời
                              </button>
                              {currentUser && currentUser.id === comment.author.id && (
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  style={{ fontSize: '12px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                                >
                                  Xóa
                                </button>
                              )}
                            </div>

                            {replyingTo === comment.id && renderReplyComposer(comment.id, comment.author.full_name || comment.author.username)}

                            {replyThread.length > 0 && (
                              <div style={{ marginTop: '12px', marginLeft: '32px' }}>
                                {visibleReplyThread.map(({ comment: reply, parentAuthor }) => (
                                  <div key={reply.id} style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <Link
                                        to={`/u/${encodeURIComponent(reply.author.username)}`}
                                        style={{ width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}
                                      >
                                        {reply.author.avatar ? (
                                          <img src={getAvatarUrl(reply.author.avatar) || ''} alt={reply.author.full_name || reply.author.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                          <div style={{ width: '100%', height: '100%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: '12px', fontWeight: 600 }}>
                                            {(reply.author.full_name || reply.author.username)[0].toUpperCase()}
                                          </div>
                                        )}
                                      </Link>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #dbeafe', padding: '12px 16px', marginBottom: '4px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '12px', fontWeight: 600, color: reply.author.role === 'admin' ? '#dc2626' : '#111827' }}>
                                              {reply.author.full_name || reply.author.username}
                                            </span>
                                            {selectedPost && reply.author.id === selectedPost.author.id && (
                                              <span style={{ padding: '2px 6px', backgroundColor: '#dbeafe', color: '#1e40af', fontSize: '12px', fontWeight: 500, borderRadius: '4px' }}>
                                                Tác giả
                                              </span>
                                            )}
                                            {reply.author.role === 'admin' && (
                                              <span style={{ padding: '2px 6px', backgroundColor: '#fee2e2', color: '#dc2626', fontSize: '12px', fontWeight: 500, borderRadius: '4px' }}>
                                                Quản trị viên
                                              </span>
                                            )}
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                              {formatDate(reply.created_at)}
                                            </span>
                                          </div>
                                          {parentAuthor && (
                                            <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: 500, marginBottom: '4px' }}>
                                              @{parentAuthor}
                                            </div>
                                          )}
                                          <div style={{ fontSize: '12px', color: '#111827', whiteSpace: 'pre-wrap', marginBottom: '8px' }}>
                                            {formatContent(reply.content)}
                                          </div>
                                          {reply.attachment_url && (
                                            <div style={{ marginTop: '8px' }}>
                                              <img src={getAvatarUrl(reply.attachment_url) || ''} alt="Attachment" style={{ maxWidth: '150px', borderRadius: '8px' }} />
                                            </div>
                                          )}
                                          {reply.link_url && (
                                            <div style={{ marginTop: '8px' }}>
                                              <a href={reply.link_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '12px', textDecoration: 'underline' }}>
                                                {reply.link_url}
                                              </a>
                                            </div>
                                          )}
                                          {reply.video_url && (
                                            <div style={{ marginTop: '8px' }}>
                                              {getVideoEmbedUrl(reply.video_url) ? (
                                                <iframe
                                                  src={getVideoEmbedUrl(reply.video_url) || ''}
                                                  title="Video"
                                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                  allowFullScreen
                                                  style={{ width: '100%', maxWidth: '300px', height: '169px', borderRadius: '8px' }}
                                                />
                                              ) : (
                                                <video src={getVideoUrl(reply.video_url) || ''} controls style={{ maxWidth: '300px', borderRadius: '8px' }} />
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                          <button
                                            onClick={() => handleToggleCommentLike(reply.id)}
                                            disabled={likingCommentId === reply.id}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: reply.is_liked ? '#f43f5e' : '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                                          >
                                            <svg
                                              style={{ width: '14px', height: '14px' }}
                                              viewBox="0 0 24 24"
                                              fill={reply.is_liked ? 'currentColor' : 'none'}
                                              stroke="currentColor"
                                              strokeWidth={1.5}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <path d="M21.8 7.112c0-2.632-2.086-4.762-4.66-4.762-1.704 0-3.188.988-3.9 2.43-.712-1.442-2.196-2.43-3.9-2.43-2.574 0-4.66 2.13-4.66 4.762 0 6.138 8.56 9.92 8.56 9.92s8.56-3.782 8.56-9.92z" />
                                            </svg>
                                            <span>{reply.likes_count ?? 0}</span>
                                          </button>
                                          <button
                                            onClick={() => setReplyingTo(replyingTo === reply.id ? null : reply.id)}
                                            style={{ fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                                          >
                                            Trả lời
                                          </button>
                                          {currentUser && currentUser.id === reply.author.id && (
                                            <button
                                              onClick={() => handleDeleteComment(reply.id)}
                                              style={{ fontSize: '12px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                                            >
                                              Xóa
                                            </button>
                                          )}
                                        </div>

                                        {replyingTo === reply.id && renderReplyComposer(reply.id, reply.author.full_name || reply.author.username)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {replyThread.length > maxCollapsedReplies && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedComments(prev => ({
                                        ...prev,
                                        [comment.id]: !isExpanded
                                      }))
                                    }
                                    style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, marginTop: '8px' }}
                                  >
                                    {isExpanded
                                      ? 'Ẩn bớt phản hồi'
                                      : `Xem thêm ${replyThread.length - maxCollapsedReplies} phản hồi`}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="post-detail-modal-overlay" style={{ zIndex: 1001 }} onClick={() => setShowLinkModal(false)}>
          <div className="post-detail-link-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="post-detail-modal-title">Thêm link</h3>
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">Nhập URL</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="post-detail-modal-input"
              />
            </div>
            <div className="post-detail-modal-footer">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLinkModal(false);
                  setLinkUrl('');
                }}
                className="post-detail-modal-cancel-button"
              >
                Hủy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (linkUrl.trim()) {
                    setShowLinkModal(false);
                  }
                }}
                className="post-detail-modal-submit-button"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="post-detail-modal-overlay" style={{ zIndex: 1001 }} onClick={() => setShowVideoModal(false)}>
          <div className="post-detail-video-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="post-detail-modal-title">Thêm video</h3>

            {/* Link Input */}
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">
                Link YouTube, Loom, Vimeo, hoặc Wistia
              </label>
              <div className="post-detail-video-link-input-wrapper">
                <svg className="post-detail-video-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="YouTube, Loom, Vimeo, or Wistia link"
                  className="post-detail-modal-input"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="post-detail-modal-divider">
              <div className="post-detail-modal-divider-line"></div>
              <span className="post-detail-modal-divider-text">hoặc</span>
              <div className="post-detail-modal-divider-line"></div>
            </div>

            {/* File Upload */}
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">Tải video lên</label>
              <div className="post-detail-video-upload-area">
                <svg className="post-detail-video-upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="post-detail-video-upload-text">Kéo thả video vào đây</p>
                <input
                  id={commentVideoFileInputId}
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setCommentVideoFile(file);
                      setVideoUrl('');
                    }
                  }}
                />
                <label
                  htmlFor={commentVideoFileInputId}
                  className="post-detail-video-upload-label"
                >
                  hoặc chọn file
                </label>
              </div>
              {commentVideoFile && (
                <div className="post-detail-video-file-info">
                  <span className="post-detail-video-file-name">{commentVideoFile.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCommentVideoFile(null);
                    }}
                    className="post-detail-video-file-remove"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="post-detail-modal-footer">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVideoModal(false);
                  setVideoUrl('');
                  setCommentVideoFile(null);
                }}
                className="post-detail-modal-cancel-button"
              >
                Hủy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoUrl.trim() || commentVideoFile) {
                    setShowVideoModal(false);
                  }
                }}
                className="post-detail-modal-submit-button"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GIF Modal */}
      {showGifModal && (
        <div className="post-detail-modal-overlay" style={{ zIndex: 1001 }} onClick={() => setShowGifModal(false)}>
          <div className="post-detail-gif-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h3 className="post-detail-modal-title">Thêm GIF từ GIPHY</h3>

            {/* Search Input */}
            <div className="post-detail-modal-body">
              <div className="post-detail-gif-search-wrapper">
                <svg className="post-detail-video-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={gifSearchQuery}
                  onChange={(e) => {
                    setGifSearchQuery(e.target.value);
                    if (e.target.value.trim()) {
                      setGifSearchMode('search');
                    } else {
                      setGifSearchMode('trending');
                      setGifSearchResults([]);
                    }
                  }}
                  placeholder="Tìm kiếm GIF..."
                  className="post-detail-modal-input"
                  style={{ paddingLeft: '40px' }}
                />
              </div>
            </div>

            {/* GIF Results */}
            <div className="post-detail-modal-body" style={{ maxHeight: '400px', overflowY: 'auto', paddingTop: '0' }}>
              {gifLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  Đang tải...
                </div>
              ) : (
                <>
                  {gifSearchMode === 'trending' && gifTrending.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>GIF đang thịnh hành</h4>
                      <div className="post-detail-gif-grid">
                        {gifTrending.map((gif: any) => (
                          <div
                            key={gif.id}
                            className="post-detail-gif-item"
                            onClick={() => handleSelectGif(gif)}
                          >
                            <img
                              src={gif.images?.fixed_height_small?.url || gif.images?.downsized_small?.url}
                              alt={gif.title || 'GIF'}
                              className="post-detail-gif-item-img"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gifSearchMode === 'search' && gifSearchResults.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                        Kết quả tìm kiếm: "{gifSearchQuery}"
                      </h4>
                      <div className="post-detail-gif-grid">
                        {gifSearchResults.map((gif: any) => (
                          <div
                            key={gif.id}
                            className="post-detail-gif-item"
                            onClick={() => handleSelectGif(gif)}
                          >
                            <img
                              src={gif.images?.fixed_height_small?.url || gif.images?.downsized_small?.url}
                              alt={gif.title || 'GIF'}
                              className="post-detail-gif-item-img"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {gifSearchMode === 'search' && gifSearchQuery.trim() && !gifLoading && gifSearchResults.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      Không tìm thấy GIF nào
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Selected GIF Preview */}
            {gifUrl && (
              <div className="post-detail-modal-body" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <label className="post-detail-modal-label">GIF đã chọn:</label>
                <div className="post-detail-gif-preview">
                  <img src={gifUrl} alt="Selected GIF" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="post-detail-modal-divider">
              <div className="post-detail-modal-divider-line"></div>
              <span className="post-detail-modal-divider-text">hoặc</span>
              <div className="post-detail-modal-divider-line"></div>
            </div>

            {/* Manual URL Input */}
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">Dán URL GIF trực tiếp</label>
              <div className="post-detail-video-link-input-wrapper">
                <svg className="post-detail-video-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <input
                  type="url"
                  value={gifUrl}
                  onChange={(e) => {
                    setGifUrl(e.target.value);
                    if (e.target.value) {
                      setGifFile(null);
                    }
                  }}
                  placeholder="https://media.giphy.com/... hoặc URL GIF trực tiếp"
                  className="post-detail-modal-input"
                />
              </div>
            </div>

            {/* File Upload */}
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">Tải GIF lên</label>
              <div className="post-detail-video-upload-area">
                <svg className="post-detail-video-upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="post-detail-video-upload-text">Kéo thả file GIF vào đây</p>
                <input
                  id={commentGifFileInputId}
                  type="file"
                  accept="image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setGifFile(file);
                      setGifUrl('');
                    }
                  }}
                />
                <label
                  htmlFor={commentGifFileInputId}
                  className="post-detail-video-upload-label"
                >
                  hoặc chọn file
                </label>
              </div>
              {gifFile && (
                <div className="post-detail-video-file-info">
                  <span className="post-detail-video-file-name">{gifFile.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setGifFile(null);
                    }}
                    className="post-detail-video-file-remove"
                  >
                    ×
                  </button>
                </div>
              )}
              {gifFile && gifPreviewUrl && (
                <div className="post-detail-gif-preview" style={{ marginTop: '12px' }}>
                  <img src={gifPreviewUrl} alt="GIF preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                </div>
              )}
            </div>

            <div className="post-detail-modal-footer">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGifModal(false);
                  setGifUrl('');
                  setGifFile(null);
                  setGifSearchQuery('');
                  setGifSearchResults([]);
                }}
                className="post-detail-modal-cancel-button"
              >
                Hủy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (gifUrl.trim() || gifFile) {
                    setShowGifModal(false);
                    setGifSearchQuery('');
                    setGifSearchResults([]);
                  }
                }}
                className="post-detail-modal-submit-button"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Link Modal */}
      {replyShowLinkModal && (
        <div className="post-detail-modal-overlay" style={{ zIndex: 1001 }} onClick={() => setReplyShowLinkModal(false)}>
          <div className="post-detail-link-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="post-detail-modal-title">Thêm link</h3>
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">Nhập URL</label>
              <input
                type="url"
                value={replyLinkUrl}
                onChange={(e) => setReplyLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="post-detail-modal-input"
              />
            </div>
            <div className="post-detail-modal-footer">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyShowLinkModal(false);
                  setReplyLinkUrl('');
                }}
                className="post-detail-modal-cancel-button"
              >
                Hủy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (replyLinkUrl.trim()) {
                    setReplyShowLinkModal(false);
                  }
                }}
                className="post-detail-modal-submit-button"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Video Modal */}
      {replyShowVideoModal && (
        <div className="post-detail-modal-overlay" style={{ zIndex: 1001 }} onClick={() => setReplyShowVideoModal(false)}>
          <div className="post-detail-video-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="post-detail-modal-title">Thêm video</h3>

            {/* Link Input */}
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">
                Link YouTube, Loom, Vimeo, hoặc Wistia
              </label>
              <div className="post-detail-video-link-input-wrapper">
                <svg className="post-detail-video-link-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <input
                  type="url"
                  value={replyVideoUrl}
                  onChange={(e) => setReplyVideoUrl(e.target.value)}
                  placeholder="YouTube, Loom, Vimeo, or Wistia link"
                  className="post-detail-modal-input"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="post-detail-modal-divider">
              <div className="post-detail-modal-divider-line"></div>
              <span className="post-detail-modal-divider-text">hoặc</span>
              <div className="post-detail-modal-divider-line"></div>
            </div>

            {/* File Upload */}
            <div className="post-detail-modal-body">
              <label className="post-detail-modal-label">Tải video lên</label>
              <div className="post-detail-video-upload-area">
                <svg className="post-detail-video-upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="post-detail-video-upload-text">Kéo thả video vào đây</p>
                <input
                  id={replyVideoFileInputId}
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setReplyVideoFile(file);
                      setReplyVideoUrl('');
                    }
                  }}
                />
                <label
                  htmlFor={replyVideoFileInputId}
                  className="post-detail-video-upload-label"
                >
                  hoặc chọn file
                </label>
              </div>
              {replyVideoFile && (
                <div className="post-detail-video-file-info">
                  <span className="post-detail-video-file-name">{replyVideoFile.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setReplyVideoFile(null);
                    }}
                    className="post-detail-video-file-remove"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="post-detail-modal-footer">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyShowVideoModal(false);
                  setReplyVideoUrl('');
                  setReplyVideoFile(null);
                }}
                className="post-detail-modal-cancel-button"
              >
                Hủy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (replyVideoUrl.trim() || replyVideoFile) {
                    setReplyShowVideoModal(false);
                  }
                }}
                className="post-detail-modal-submit-button"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;

